// React
import { useEffect, useRef, useState } from 'react';

// Components
import { VisibilityOffOutlined, VisibilityOutlined, EmailOutlined, LockOutlined } from '@mui/icons-material';
import { Grid, IconButton, InputAdornment, TextField } from "@mui/material";

// Style
import { red } from '@mui/material/colors/index.js';
import { useAppTheme } from '../../../hooks/useAppTheme.js';

function LoginForm(props) {
	const { doLogin, email, password, setEmail, setPassword, loginFailed, setLoginFailed, isDarkMode } = props;

	const [showPassword, setShowPassword] = useState(false);
	const [focusedField, setFocusedField] = useState(null);
	const emailInputRef = useRef(null);
	const passwordInputRef = useRef(null);
	
	const { colors } = useAppTheme();

	const onChangePassword = (event) => {
		setPassword(event.target.value);
	}

	const onChangeEmail = (event) => {
		setEmail(String(event.target.value).toLocaleLowerCase());
	}

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			doLogin();
		}
	}

	const handleMouseDownPassword = (event) => {
		// Only prevent focus-stealing on the visibility icon itself
		if (event.button === 0) event.preventDefault();
	}

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	}

	// Some browsers/password managers autofill without firing onChange.
	// Sync DOM values into React state so buttons/validation work immediately.
	useEffect(() => {
		const syncFromDom = () => {
			const domEmail = emailInputRef.current?.value;
			const domPassword = passwordInputRef.current?.value;
			if (typeof domEmail === 'string' && domEmail !== '' && domEmail !== email) {
				setEmail(domEmail.toLocaleLowerCase());
			}
			if (typeof domPassword === 'string' && domPassword !== '' && domPassword !== password) {
				setPassword(domPassword);
			}
		};

		// Try a few times shortly after mount (covers delayed autofill)
		let tries = 0;
		syncFromDom();
		const id = setInterval(() => {
			syncFromDom();
			tries += 1;
			if (tries >= 10) clearInterval(id);
		}, 200);
		return () => clearInterval(id);
	}, [email, password, setEmail, setPassword]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<TextField 
					fullWidth
					variant="outlined" 
					type="email" 
					label="E-Mail" 
					name="email"
					autoComplete="username"
					value={ email || '' } 
					onChange={ onChangeEmail } 
					onKeyDown={ handleKeyDown }
					onFocus={ () => { setFocusedField('email'); if (loginFailed) setLoginFailed(false); } }
					onBlur={ () => setFocusedField(null) }
					error={ loginFailed }
					inputRef={ emailInputRef }
					InputLabelProps={{
						sx: {
							color: loginFailed ? red[600] : (email ? colors.login.input.text : colors.login.input.label),
							'&.Mui-focused': {
								color: loginFailed ? `${red[600]} !important` : `${colors.login.input.borderFocus} !important`,
							},
							'&.Mui-error': {
								color: `${red[600]} !important`,
							}
						}
					}}
					InputProps={{
						startAdornment: (
							<EmailOutlined sx={{ mr: 1, color: colors.login.input.icon }} />
						),
						sx: {
							color: colors.login.input.text,
						}
					}}
					sx={{
						'& .MuiOutlinedInput-root': {
							borderRadius: 3,
							backgroundColor: colors.login.input.bg,
							'& fieldset': {
								borderColor: loginFailed ? red[600] : (focusedField === 'email' ? colors.login.input.borderFocus : (email ? colors.login.input.success : colors.login.input.border)),
							},
							'&:hover fieldset': {
								borderColor: loginFailed ? red[600] : (focusedField === 'email' ? colors.login.input.borderFocus : (email ? colors.login.input.success : colors.login.input.borderHover)),
							},
							'&.Mui-focused fieldset': {
								borderColor: loginFailed ? red[600] : colors.login.input.borderFocus,
							},
							'&:has(input:-webkit-autofill)': {
								backgroundColor: colors.login.input.bg,
							},
							'&:has(input:-webkit-autofill:hover)': {
								backgroundColor: colors.login.input.bgHover,
							},
							'&:has(input:-webkit-autofill:focus)': {
								backgroundColor: colors.login.input.bgFocus,
							},
						},
						'& input:-webkit-autofill': {
							WebkitBoxShadow: isDarkMode ? `0 0 0 100px transparent inset !important` : '0 0 0 100px transparent inset !important',
							WebkitTextFillColor: isDarkMode ? `white !important` : 'inherit !important',
							transition: 'background-color 5000s ease-in-out 0s',
						},
					}}
				/>
			</Grid>

			<Grid item xs={12}>
				<TextField 
					fullWidth
					variant="outlined" 
					type={ showPassword ? 'text' : 'password' } 
					label="Passwort" 
					name="password"
					autoComplete="current-password"
					value={ password } 
					onChange={ onChangePassword } 
					onKeyDown={ handleKeyDown }
					onFocus={ () => { setFocusedField('password'); if (loginFailed) setLoginFailed(false); } }
					onBlur={ () => setFocusedField(null) }
					error={ loginFailed }
					inputRef={ passwordInputRef }
					InputLabelProps={{
						sx: {
							color: loginFailed ? red[600] : (password ? colors.login.input.text : colors.login.input.label),
							'&.Mui-focused': {
								color: loginFailed ? `${red[600]} !important` : `${colors.login.input.borderFocus} !important`,
							},
							'&.Mui-error': {
								color: `${red[600]} !important`,
							}
						}
					}}
					InputProps={{
						startAdornment: (
							<LockOutlined sx={{ mr: 1, color: colors.login.input.icon }} />
						),
						endAdornment: (
							<InputAdornment position="end">
								<IconButton 
									onClick={ handleClickShowPassword } 
									onMouseDown={ handleMouseDownPassword } 
									edge="end"
									sx={{ color: colors.login.input.icon }}
								>
									{ showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined /> }
								</IconButton>
							</InputAdornment>
						),
						sx: {
							color: colors.login.input.text,
						}
					}}
					sx={{
						'& .MuiOutlinedInput-root': {
							borderRadius: 3,
							backgroundColor: colors.login.input.bg,
							'& fieldset': {
								borderColor: loginFailed ? red[600] : (focusedField === 'password' ? colors.login.input.borderFocus : (password ? colors.login.input.success : colors.login.input.border)),
							},
							'&:hover fieldset': {
								borderColor: loginFailed ? red[600] : (focusedField === 'password' ? colors.login.input.borderFocus : (password ? colors.login.input.success : colors.login.input.borderHover)),
							},
							'&.Mui-focused fieldset': {
								borderColor: loginFailed ? red[600] : colors.login.input.borderFocus,
							},
							'&:has(input:-webkit-autofill)': {
								backgroundColor: colors.login.input.bg,
							},
							'&:has(input:-webkit-autofill:hover)': {
								backgroundColor: colors.login.input.bgHover,
							},
							'&:has(input:-webkit-autofill:focus)': {
								backgroundColor: colors.login.input.bgFocus,
							},
							// Force background to stay glassy
							'&.Mui-filled': {
								backgroundColor: colors.login.input.bg
							},
							'&.Mui-focused': {
								backgroundColor: colors.login.input.bg
							}
						},
						'& input:-webkit-autofill': {
							WebkitBoxShadow: isDarkMode ? `0 0 0 100px transparent inset !important` : '0 0 0 100px transparent inset !important',
							WebkitTextFillColor: isDarkMode ? `white !important` : 'inherit !important',
							transition: 'background-color 5000s ease-in-out 0s',
						},
					}}
				/>
			</Grid>
		</Grid>
	)
}

export default LoginForm;